import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import type { User } from "@common/types/user";
import type { PresenceStackMember } from "@components/avatar/PresenceStack";
import {
    buildRolesByUserId,
    deduplicateMembers,
} from "@components/organizationView/clients-closes/cells/closeOverviewMemberHelpers";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";

const VIEWING_DETAIL = "Viewing a task";

const AGENT_MEMBER: PresenceStackMember = {
    kind: "agent",
    detail: "Working now",
    isLive: true,
};

/** Someone viewing the work now, as the organization's members list them. */
export type WorkCardViewer = Pick<
    User,
    "id" | "name" | "avatar" | "iconColor" | "email"
>;

const getMemberName = (member: CloseOverviewMember) =>
    member.preferredName ?? member.email;

/**
 * A Work card's presence stack, left to right: the agent while it works,
 * the people viewing now, then the engagement team with their roles. A team
 * member who is viewing one of its tasks shows once, as team, with
 * ` · Viewing a task`.
 */
export const getWorkCardPresenceMembers = ({
    row,
    isAgentWorking,
    viewers,
}: {
    row: Pick<
        ClientsWorkOverviewWorkRow,
        "preparers" | "reviewers" | "managers"
    >;
    isAgentWorking: boolean;
    viewers: readonly WorkCardViewer[];
}): PresenceStackMember[] => {
    const { preparers, reviewers, managers } = row;
    const rolesByUserId = buildRolesByUserId(preparers, reviewers, managers);
    const teamMembers = deduplicateMembers([
        ...preparers,
        ...reviewers,
        ...managers,
    ]);
    const viewerIds = new Set(viewers.map(({ id }) => id));
    const teamIds = new Set(teamMembers.map(({ userId }) => userId));

    const team = teamMembers.map((member): PresenceStackMember => {
        const roles = rolesByUserId.get(member.userId)?.join(", ") ?? "";
        const isViewing = viewerIds.has(member.userId);
        const name = getMemberName(member);
        return {
            kind: "person",
            id: String(member.userId),
            name,
            user: {
                name,
                email: member.email,
                avatar: member.avatar,
                iconColor: member.iconColor,
            },
            role: "team",
            detail: isViewing ? `${roles} · ${VIEWING_DETAIL}` : roles,
            isLive: isViewing,
        };
    });
    const otherViewers = viewers
        .filter(({ id }) => !teamIds.has(id))
        .map((viewer): PresenceStackMember => ({
            kind: "person",
            id: String(viewer.id),
            name: viewer.name,
            user: viewer,
            role: "viewer",
            detail: VIEWING_DETAIL,
            isLive: true,
        }));

    return [
        ...(isAgentWorking ? [AGENT_MEMBER] : []),
        ...otherViewers,
        ...team,
    ];
};

/**
 * A List row's presence stack members: the card's, in its order, without
 * the team members who aren't viewing, since the Engagement team column
 * shows them.
 */
export const getWorkRowPresenceMembers = (
    presence: Parameters<typeof getWorkCardPresenceMembers>[0],
): PresenceStackMember[] =>
    getWorkCardPresenceMembers(presence).filter(
        (member) =>
            member.kind !== "person" || member.role !== "team" || member.isLive,
    );
